import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, UserCheck, MessageCircle, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { SOCIALS, socialHref, hasSocials } from "@/lib/socials";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all";

export default function Profile() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const isMe = user.id === id;

  const load = useCallback(async () => {
    const { data } = await api.get(`/users/${id}`);
    setProfile(data); setEdit(data);
    const { data: allPosts } = await api.get("/posts");
    setPosts(allPosts.filter((p) => p.author?.id === id));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const toggleFollow = async () => {
    const { data } = await api.post(`/users/${id}/follow`);
    setProfile((p) => ({ ...p, followers: data.following ? [...p.followers, user.id] : p.followers.filter((f) => f !== user.id) }));
  };

  const save = async () => {
    const { data } = await api.put("/users/me", { name: edit.name, bio: edit.bio, avatar: edit.avatar, category: edit.category, company: edit.company, socials: edit.socials || {} });
    setProfile(data); setUser(data); setOpen(false); toast.success("Profile updated!");
  };

  const setSocial = (key) => (e) => setEdit((prev) => ({ ...prev, socials: { ...(prev.socials || {}), [key]: e.target.value } }));

  if (!profile) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>;
  const following = profile.followers.includes(user.id);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="h-32 brand-gradient" />
        <div className="px-6 pb-6">
          <div className="flex items-end justify-between -mt-12">
            <Avatar className="h-24 w-24 ring-4 ring-white"><AvatarImage src={profile.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold text-3xl">{(profile.name||"U")[0]}</AvatarFallback></Avatar>
            {isMe ? (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="edit-profile-btn" className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm bg-slate-100 hover:bg-pink-100 text-slate-700 hover:text-pink-600 transition-all"><Pencil className="h-4 w-4" /> Edit</button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl max-w-md">
                  <DialogHeader><DialogTitle className="font-display text-xl">Edit profile</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <input data-testid="edit-name" placeholder="Name" className={inputCls} value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                    <textarea data-testid="edit-bio" placeholder="Bio" rows={3} className={inputCls} value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} />
                    <input data-testid="edit-avatar" placeholder="Avatar image URL" className={inputCls} value={edit.avatar || ""} onChange={(e) => setEdit({ ...edit, avatar: e.target.value })} />
                    {profile.role === "brand"
                      ? <input data-testid="edit-company" placeholder="Company" className={inputCls} value={edit.company || ""} onChange={(e) => setEdit({ ...edit, company: e.target.value })} />
                      : <input data-testid="edit-category" placeholder="Niche" className={inputCls} value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} />}
                    <div className="pt-1">
                      <p className="text-sm font-bold text-slate-700 mb-2">Link social accounts</p>
                      <div className="space-y-2">
                        {SOCIALS.map((s) => {
                          const Icon = s.icon;
                          return (
                            <div key={s.key} className="flex items-center gap-2">
                              <div className="h-10 w-10 shrink-0 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500"><Icon className="h-4 w-4" /></div>
                              <input data-testid={`edit-social-${s.key}`} placeholder={`${s.label} (${s.ph})`} className={inputCls}
                                value={(edit.socials || {})[s.key] || ""} onChange={setSocial(s.key)} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <button data-testid="edit-save" onClick={save} className="w-full py-3 rounded-full font-bold text-white brand-gradient">Save changes</button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="flex gap-2">
                <button data-testid="profile-follow" onClick={toggleFollow} className={`flex items-center gap-1.5 px-5 py-2 rounded-full font-bold text-sm transition-all ${following ? "bg-slate-100 text-slate-600" : "brand-gradient text-white shadow-md shadow-pink-500/30"}`}>
                  {following ? <><UserCheck className="h-4 w-4" /> Following</> : <><UserPlus className="h-4 w-4" /> Follow</>}
                </button>
                <button data-testid="profile-message" onClick={() => nav(`/messages?to=${profile.id}`)} className="h-9 w-9 rounded-full bg-slate-100 hover:bg-pink-100 flex items-center justify-center text-slate-600 hover:text-pink-600"><MessageCircle className="h-4 w-4" /></button>
              </div>
            )}
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-2xl font-extrabold text-slate-900">{profile.name}</h1>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${profile.role === "brand" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{profile.role}</span>
            </div>
            <p className="text-sm text-slate-400">{profile.role === "brand" ? profile.company : profile.category}</p>
            {profile.bio && <p className="text-slate-600 mt-2">{profile.bio}</p>}
            <div className="flex gap-6 mt-3 text-sm">
              <span><b className="text-slate-900">{profile.followers.length}</b> <span className="text-slate-400">followers</span></span>
              <span><b className="text-slate-900">{profile.following.length}</b> <span className="text-slate-400">following</span></span>
              <span><b className="text-slate-900">{posts.length}</b> <span className="text-slate-400">posts</span></span>
            </div>
            {profile.socials && hasSocials(profile.socials) && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="profile-socials">
                {SOCIALS.map((s) => {
                  const href = socialHref(s.key, profile.socials[s.key]);
                  if (!href) return null;
                  const Icon = s.icon;
                  return (
                    <a key={s.key} href={href} target="_blank" rel="noopener noreferrer" title={s.label} data-testid={`social-link-${s.key}`}
                      className={`h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 transition-all duration-200 ${s.tint}`}>
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-display font-bold text-xl mt-8 mb-4 text-slate-900">Posts</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {posts.map((p) => (
          <div key={p.id} className="aspect-square rounded-xl overflow-hidden bg-white border border-slate-100">
            {p.image ? <img src={p.image} alt="" className="h-full w-full object-cover" /> : <div className="p-3 text-xs text-slate-500">{p.content}</div>}
          </div>
        ))}
        {posts.length === 0 && <p className="text-slate-400 col-span-full">No posts yet.</p>}
      </div>
    </div>
  );
}
